// ============================================
// Loyalty Hooks - TanStack Query
// ============================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type {
  LoyaltyAccountListParams,
  LoyaltyEntryListParams,
  LoyaltyTier,
  UpdateTierConfigRequest,
  EarnPointsRequest,
  RedeemPointsRequest,
  UUID,
} from '@conai/shared-types';
import { useLoyaltyService } from '../providers/ApiProvider';
import { queryKeys } from '../config/query-client';
import { useI18n } from '../i18n';

/**
 * Hook to list loyalty accounts with pagination and filters
 */
export function useLoyaltyAccounts(params: LoyaltyAccountListParams = {}) {
  const loyaltyService = useLoyaltyService();

  return useQuery({
    queryKey: queryKeys.loyalty.list(params as Record<string, unknown>),
    queryFn: () => loyaltyService.listAccounts(params),
  });
}

/**
 * Hook to list loyalty accounts by tier
 */
export function useLoyaltyAccountsByTier(
  tier: LoyaltyTier,
  params: Omit<LoyaltyAccountListParams, 'tier'> = {}
) {
  const loyaltyService = useLoyaltyService();

  return useQuery({
    queryKey: queryKeys.loyalty.list({ ...params, tier }),
    queryFn: () => loyaltyService.getAccountsByTier(tier, params),
  });
}

/**
 * Hook to get a single loyalty account by ID
 */
export function useLoyaltyAccount(id: string, options?: { enabled?: boolean }) {
  const loyaltyService = useLoyaltyService();

  return useQuery({
    queryKey: queryKeys.loyalty.detail(id),
    queryFn: () => loyaltyService.getAccountById(id),
    enabled: options?.enabled !== false && !!id,
  });
}

/**
 * Hook to get loyalty account by customer ID
 */
export function useLoyaltyAccountByCustomer(customerId: UUID | undefined) {
  const loyaltyService = useLoyaltyService();

  return useQuery({
    queryKey: queryKeys.loyalty.balance(customerId || ''),
    queryFn: () => loyaltyService.getAccountByCustomer(customerId!),
    enabled: !!customerId,
  });
}

/**
 * Hook to get points history for an account
 */
export function usePointsHistory(
  accountId: string,
  params: LoyaltyEntryListParams = {},
  options?: { enabled?: boolean }
) {
  const loyaltyService = useLoyaltyService();

  return useQuery({
    queryKey: queryKeys.loyalty.history(accountId, params as Record<string, unknown>),
    queryFn: () => loyaltyService.getPointsHistory(accountId, params),
    enabled: options?.enabled !== false && !!accountId,
  });
}

/**
 * Hook to earn points for a customer
 */
export function useEarnPoints() {
  const loyaltyService = useLoyaltyService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: EarnPointsRequest) =>
      loyaltyService.earnPoints(data) as unknown as Promise<{ points: number }>,
    onSuccess: (result) => {
      // Invalidate all loyalty related lists since we don't have accountId
      queryClient.invalidateQueries({ queryKey: queryKeys.loyalty.lists() });
      toast.success(`Earned ${result.points} points`);
    },
  });
}

/**
 * Hook to redeem points for discount
 */
export function useRedeemPoints() {
  const loyaltyService = useLoyaltyService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ accountId, data }: { accountId: string; data: RedeemPointsRequest }) =>
      loyaltyService.redeemPoints(accountId, data) as unknown as Promise<{ points: number; discount_value: string }>,
    onSuccess: (result, { accountId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.loyalty.detail(accountId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.loyalty.history(accountId, {}) });
      queryClient.invalidateQueries({ queryKey: queryKeys.loyalty.lists() });
      toast.success(`Redeemed ${result.points} points for ${result.discount_value} discount`);
    },
  });
}

/**
 * Chỉnh điểm THỦ CÔNG (sheet 2 mục #44) — contract BE chốt 03/09/2026:
 * MỘT endpoint cho cả cộng lẫn trừ: POST /loyalty/adjust/
 * { customer_id, points (± khác 0), reason (bắt buộc, vào audit log) }.
 * Trừ quá số dư bị BE từ chối. Role: STORE_ADMIN / AI_ADMIN.
 * (Bản trước đi qua accounts/{id}/earn — endpoint đó CHƯA TỪNG tồn tại,
 * chỉ có trong API_REFERENCE cũ; và redeem — sai ngữ nghĩa. Đã bỏ cả hai.)
 */
export function useAdjustAccountPoints() {
  const loyaltyService = useLoyaltyService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      customerId,
      points,
      reason,
    }: {
      customerId: string;
      /** Tài khoản điểm — chỉ để invalidate cache đúng chỗ. */
      accountId: string;
      /** Dương = cộng, âm = trừ. Không nhận 0. */
      points: number;
      reason: string;
    }) => loyaltyService.adjustPoints({ customer_id: customerId, points, reason }),
    onSuccess: (_result, { accountId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.loyalty.detail(accountId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.loyalty.history(accountId, {}) });
      queryClient.invalidateQueries({ queryKey: queryKeys.loyalty.lists() });
      toast.success('Points adjusted');
    },
  });
}

/**
 * TẶNG điểm thưởng (sheet 2 mục #44) — POST /loyalty/bonus/, BE chốt 09/09/2026.
 *
 * Tách khỏi `useAdjustAccountPoints` vì hai việc khác nghĩa: `ADJUST` là sửa
 * sai số, `BONUS` là tặng thêm theo chiến dịch. Gộp lại thì báo cáo không tách
 * được chi phí khuyến mãi khỏi số điểm sửa lỗi. `/loyalty/adjust/` cũng không
 * nhận `entry_type`, nên đây là đường DUY NHẤT sinh được entry `BONUS`.
 *
 * `points` luôn dương — tặng không có chiều âm.
 */
export function useGrantBonusPoints() {
  const loyaltyService = useLoyaltyService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      customerId,
      points,
      reason,
    }: {
      customerId: string;
      /** Tài khoản điểm — chỉ để invalidate cache đúng chỗ. */
      accountId: string;
      /** LUÔN DƯƠNG. */
      points: number;
      reason: string;
    }) => loyaltyService.grantBonusPoints({ customer_id: customerId, points, reason }),
    onSuccess: (_result, { accountId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.loyalty.detail(accountId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.loyalty.history(accountId, {}) });
      queryClient.invalidateQueries({ queryKey: queryKeys.loyalty.lists() });
      toast.success('Bonus points granted');
    },
  });
}

/**
 * Hook to calculate loyalty statistics
 */
export function useLoyaltyStats() {
  const { data: allAccounts, isLoading: allLoading } = useLoyaltyAccounts({ page_size: 1 });
  const { data: bronzeAccounts, isLoading: bronzeLoading } = useLoyaltyAccountsByTier('BRONZE', { page_size: 1 });
  const { data: silverAccounts, isLoading: silverLoading } = useLoyaltyAccountsByTier('SILVER', { page_size: 1 });
  const { data: goldAccounts, isLoading: goldLoading } = useLoyaltyAccountsByTier('GOLD', { page_size: 1 });
  const { data: platinumAccounts, isLoading: platinumLoading } = useLoyaltyAccountsByTier('PLATINUM', { page_size: 1 });

  const isLoading = allLoading || bronzeLoading || silverLoading || goldLoading || platinumLoading;

  const stats = {
    totalMembers: allAccounts?.count || 0,
    bronze: bronzeAccounts?.count || 0,
    silver: silverAccounts?.count || 0,
    gold: goldAccounts?.count || 0,
    platinum: platinumAccounts?.count || 0,
  };

  return {
    stats,
    isLoading,
  };
}

// ============================================
// Cấu hình ngưỡng hạng (sheet 2 mục #45)
// ============================================
//
// Tên `useTierConfig` trước đây là một bọc quanh mấy helper tính điểm ở client
// (trong đó có `getPointsForNextTier` hardcode ngưỡng). Không nơi nào dùng, và
// bản thân hàm đó đã bị xoá khỏi api-client — nay tên này dùng cho query thật.

/** Ngưỡng hạng theo từng tiền tệ. Gồm cả dòng mặc định — đọc `source` để phân biệt. */
export function useTierConfig(options?: { enabled?: boolean }) {
  const loyaltyService = useLoyaltyService();

  return useQuery({
    queryKey: queryKeys.loyalty.tierConfig(),
    queryFn: () => loyaltyService.getTierConfig(),
    enabled: options?.enabled !== false,
    // Endpoint chưa deploy QA -> 404; đừng retry dồn dập, để màn hình vào
    // error state ngay.
    retry: false,
  });
}

/**
 * Lưu ngưỡng cho một tiền tệ.
 *
 * ⚠️ CHỈ invalidate cấu hình, KHÔNG invalidate danh sách tài khoản: hạng của
 * khách hiện hữu KHÔNG đổi khi lưu. Refetch danh sách ở đây sẽ tạo cảm giác
 * "vừa cập nhật xong" trong khi thực tế chưa có gì đổi — đúng cái hiểu nhầm cần
 * tránh. Nút "Tính lại toàn bộ" mới là chỗ làm việc đó.
 */
export function useUpdateTierConfig() {
  const loyaltyService = useLoyaltyService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: ({ currency, data }: { currency: string; data: UpdateTierConfigRequest }) =>
      loyaltyService.updateTierConfig(currency, data),
    onSuccess: (_result, { currency }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.loyalty.tierConfig() });
      toast.success(t('tierCfgToastSaved', { currency }));
    },
  });
}

/** Xoá ngưỡng riêng, quay về bảng mặc định của BE. Cũng không đổi hạng khách hiện hữu. */
export function useResetTierConfig() {
  const loyaltyService = useLoyaltyService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (currency: string) => loyaltyService.resetTierConfig(currency),
    onSuccess: (_result, currency) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.loyalty.tierConfig() });
      toast.success(t('tierCfgToastReset', { currency }));
    },
  });
}

/**
 * Tính lại hạng cho toàn bộ khách theo ngưỡng đang hiệu lực.
 *
 * Đây là thao tác DUY NHẤT thật sự đổi hạng khách, nên invalidate cả gốc
 * `loyalty` (danh sách tài khoản, thống kê theo hạng, lịch sử điểm).
 */
export function useRecalculateTiers() {
  const loyaltyService = useLoyaltyService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: () => loyaltyService.recalculateTiers(),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.loyalty.all });
      const updated = result?.updated_count ?? result?.processed_count;
      toast.success(
        updated != null
          ? t('tierCfgToastRecalculatedCount', { count: updated })
          : t('tierCfgToastRecalculated')
      );
    },
  });
}

/**
 * Kiểm tra ngưỡng ở CLIENT, giống hệt luật của BE (`0 < silver < gold < platinum`).
 *
 * Có để báo lỗi ngay tại ô nhập thay vì đợi 400 chung chung. BE vẫn là nơi chốt
 * — đây chỉ là lớp báo sớm, không phải nơi nới lỏng luật.
 * Trả về key i18n của lỗi, hoặc `null` khi hợp lệ.
 */
export function validateTierThresholds(values: UpdateTierConfigRequest): string | null {
  const { silver_threshold, gold_threshold, platinum_threshold } = values;
  if (
    !Number.isFinite(silver_threshold) ||
    !Number.isFinite(gold_threshold) ||
    !Number.isFinite(platinum_threshold)
  ) {
    return 'tierCfgErrorNumber';
  }
  if (silver_threshold <= 0) return 'tierCfgErrorSilverPositive';
  if (gold_threshold <= silver_threshold) return 'tierCfgErrorOrder';
  if (platinum_threshold <= gold_threshold) return 'tierCfgErrorOrder';
  return null;
}

/**
 * Get tier display info
 */
export function getTierInfo(tier: LoyaltyTier): {
  label: string;
  color: string;
  bgColor: string;
  icon: string;
} {
  switch (tier) {
    case 'PLATINUM':
      return {
        label: 'Platinum',
        color: 'text-slate-700',
        bgColor: 'bg-gradient-to-r from-slate-200 to-slate-300',
        icon: '💎',
      };
    case 'GOLD':
      return {
        label: 'Gold',
        color: 'text-amber-700',
        bgColor: 'bg-gradient-to-r from-amber-200 to-amber-300',
        icon: '🏆',
      };
    case 'SILVER':
      return {
        label: 'Silver',
        color: 'text-gray-600',
        bgColor: 'bg-gradient-to-r from-gray-200 to-gray-300',
        icon: '🥈',
      };
    case 'BRONZE':
    default:
      return {
        label: 'Bronze',
        color: 'text-orange-700',
        bgColor: 'bg-gradient-to-r from-orange-200 to-orange-300',
        icon: '🥉',
      };
  }
}
